import React, { useMemo } from 'react'
import { ColDef } from 'ag-grid-community'
import { HStack } from '@musinsa/mwms-ds'
import GridTable from '@/components/table/GridTable'
import { OutboundsItem } from '@/services/outbound/outboundSearch/types'
import { ExcelDownloadType } from '@/services/excel/excel.types'
import { GRID_SIZE } from '@/utils/grid/grid'
import { ORDER_TYPES } from '@/services/outbound/outboundWaitingStatus/consts'
import { formatGlobalTransferStatus } from '@/services/outbound/outboundSearch/consts'

interface OutboundSearchBatchTableProps {
  items: OutboundsItem[]
  pageInfo?: PageInfoType
  isLoading?: boolean
  currentPage?: number
  onChangePage?: (page: number) => void
  onExportData?: (params?: ExcelDownloadType) => void
}
export const OutboundSearchOrderTable = ({
  items,
  pageInfo,
  isLoading,
  currentPage,
  onChangePage,
  onExportData,
}: OutboundSearchBatchTableProps) => {
  const columnDefs = useMemo<ColDef<OutboundsItem>[]>(() => {
    return [
      // 고정 열을 더 늘리지 않는다 — 1280px 창에서 고정 열 합이 그리드 폭을 넘으면 나머지 열이 보이지 않는다.
      // 고정 해제 열의 맨 앞에 두어 고정 열 「주문일련번호」 바로 오른쪽에 보이게 한다.
      { field: 'globalOrderNumber', headerName: 'Bizest 주문번호', width: 200 },
      { field: 'globalOrderOptionNumber', headerName: 'Bizest 주문일련번호', width: 150 },
      { field: 'logisticsServiceType', headerName: '화주유형', width: 120 },
      { field: 'shipperName', headerName: '화주명', width: 120 },
      { field: 'salChannel', headerName: '판매채널명', width: 120 },
      { field: 'cutDate', headerName: '출고등록일', cellDataType: 'dateNday', width: 120, pinned: 'left' },
      { field: 'collectedDate', headerName: '주문수집일', cellDataType: 'dateNday', width: 120 },
      { field: 'actualDate', headerName: '출고실적일', cellDataType: 'dateNday', width: 120, pinned: 'left' },
      { field: 'orderStatus', headerName: '주문상태', width: 100, pinned: 'left' },
      { field: 'globalTransferStatus', headerName: '이관상태', width: 100, valueFormatter: ({ data }) => formatGlobalTransferStatus(data?.globalTransferStatus) },
      { field: 'orderQuantity', headerName: '주문수량', width: 100 },
      { field: 'allocatedQuantity', headerName: '할당수량', width: 100 },
      { field: 'actualQuantity', headerName: '출고수량', width: 100 },
      { field: 'transportType', headerName: '배송유형', width: 100 },
      { field: 'orderType', headerName: '주문유형', width: 100 },
      { field: 'cutOffNo', headerName: '차수명', width: 250 },
      { field: 'orderNumber', headerName: '주문번호', width: 200, pinned: 'left' },
      { field: 'orderOptionNumber', headerName: '주문일련번호', width: 130, pinned: 'left' },
      { field: 'refNo1', headerName: '화주사주문번호', width: 140 },
      { field: 'refNo2', headerName: '화주사주문일련번호', width: 140 },
      { field: 'etc1', headerName: '기타1', width: 140 },
      { field: 'etc2', headerName: '기타2', width: 140 },
      { field: 'goodsCode', headerName: '상품코드', width: 130 },
      { field: 'invoiceNumber', headerName: '송장번호', width: 130 },
      { field: 'uid', headerName: 'UID', width: 130 },
      { field: 'brandName', headerName: '브랜드', width: 150 },
      { field: 'styleNo', headerName: '스타일넘버', width: 120 },
      { field: 'barcode', headerName: '바코드', width: 130 },
      { field: 'skuId', headerName: 'SKUID', width: 150 },
      { field: 'optionCode', headerName: '옵션코드', width: 130 },
      { field: 'goodsName', headerName: '상품명', width: 300 },
      { field: 'goodsOption', headerName: '옵션', width: 120 },
      { field: 'gift', headerName: '사은품', width: 150 },
      { field: 'orderUserName', headerName: '주문자', width: 100, cellDataType: 'maskName' },
      { field: 'receiverName', headerName: '수령자', width: 100, cellDataType: 'maskName' },
      { field: 'receiverMobile', headerName: '핸드폰', width: 120, cellDataType: 'maskPhone' },
      { field: 'receiverPhone', headerName: '전화번호', width: 120, cellDataType: 'maskPhone' },
      { field: 'receiverZipcode', headerName: '우편번호', width: 90, cellDataType: 'maskPostalCode' },
      { field: 'receiverAddress', headerName: '주소', width: 400, cellDataType: 'maskAddress' },
      { field: 'warehouseName', headerName: '출고센터', width: 120 },
      { field: 'deliveryMessage', headerName: '출고메시지', width: 250 },
      { field: 'orderConsolNumber', headerName: '통합출고번호', width: 140 },
      { field: 'carrier', headerName: '운송사', width: 100 },
      {
        field: 'deliveryCompletePlanDate',
        headerName: '도착보장일',
        cellDataType: 'dateNday',
        width: 120,
        valueGetter: (params) => (params.data?.orderType === ORDER_TYPES.PLUS ? params.data.deliveryCompletePlanDate : null),
      },
      { field: 'outboundDeadlineDate', headerName: '출고마감일', cellDataType: 'dateNday', width: 120 },
      {
        field: 'isHolidayDeliveryAvailable',
        headerName: '휴일배송여부',
        width: 120,
        cellDataType: 'yn',
      },
    ]
  }, [])


  return (
    <HStack full gap={16}>
      <GridTable<OutboundsItem>
        isColumnNumPinned
        useColumnNo
        rowSelectable
        title="차수 상세"
        isLoading={isLoading}
        rowData={items}
        exportType="private"
        exportServerAudit
        exportPersonalInfoOption
        onExportData={onExportData}
        onChangePage={onChangePage}
        // buttons={[{ disabled: true, fit: true, kind: 'secondary', content: '주문 취소', onClick: () => {} }]} // TODO: [SCOPE B] 차수 취소 버튼 기능 추가 예정
        countPerPage={GRID_SIZE.MEDIUM}
        currentPage={currentPage || 0}
        totalPages={pageInfo?.totalPages}
        totalCount={pageInfo?.totalCount || 0}
       columnDefs={columnDefs}
       gridKey="GI25_HEAD"
       legacyStorageKey="outbound-search-order-table"
     />
    </HStack>
  )
}
